<template>
  <vue-final-modal
    :model-value="modelValue"
    :modal-id="modalId"
    :hide-overlay="hideOverlay"
    :overlay-class="overlayClass"
    :overlay-style="overlayStyle"
    :horizontal-align="horizontalAlign"
    :content-class="[isTouch ? 'touch-device' : '', contentClass || $attrs['content-class'] || $attrs.contentClass]"
    :content-style="{ width: modalWidth }"
    :content-transition="resolvedContentTransition"
    :click-to-close="!persistent"
    :esc-to-close="!persistent"
    @update:model-value="closeModal"
  >
    <component
      :is="modalTemplate"
      v-bind="props.props"
      :style="{ width: modalWidth }"
      :class="{ 'custom-modal--big-size': isBigSize }"
      @on-ok="onOk"
      @close-modal="closeModal"
    >
      <template #closeButton>
        <div v-if="!slots.header && !title" class="custom-modal__close-btn" data-name="close-modal" @click="closeModal">
          <icon name="close" />
        </div>
      </template>

      <template #header>
        <div v-if="slots.header || title" class="custom-modal__header-wrapper text-adaptive-h2">
          <ol-safe-html v-if="title" class="custom-modal__header" :html="title" />
          <slot name="header" />
          <div class="custom-modal__close-btn" @click="closeModal">
            <icon name="close" />
          </div>
        </div>
      </template>

      <template #body>
        <ol-safe-html
          v-if="bodyHtml"
          class="custom-modal__body"
          :class="{ 'custom-modal__body--ios': $helpers.platform.is.ios }"
          :html="bodyHtml"
        />
        <slot v-else-if="customBody"></slot>
        <div v-else class="custom-modal__body" :class="{ 'custom-modal__body--ios': $helpers.platform.is.ios }">
          <slot></slot>
          {{ body }}
        </div>
        <div v-if="!footer && actions" class="custom-modal__actions">
          <ol-button v-if="hasClose" variant="secondary" :label="$t('close')" @click="closeModal" />
          <ol-button
            v-for="(button, index) in actions"
            :key="index"
            :class="button.class"
            v-bind="button.props"
            @click="button.action"
          />
        </div>
      </template>

      <template v-if="hasFooter" #footer>
        <ol-button v-if="hasClose" :label="$t('close')" variant="secondary" @click="closeModal" />
        <ol-button
          v-for="(button, index) in actions"
          :key="index"
          v-bind="button"
          :loading="loadingObject[index]"
          :disabled="button.disabled"
          @click="() => modalAction(index, button)"
        />
        <slot name="footer"></slot>
      </template>
    </component>
  </vue-final-modal>
</template>

<script setup>
import { computed, defineAsyncComponent, inject, markRaw, ref, useSlots, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { OlButton, OlSafeHtml } from '@gamesite/game-core'
import { VueFinalModal } from 'vue-final-modal'
import { useSiteInfoStore } from 'src/stores/site-info'
import { useHelpers } from 'src/common/composition/useHelpers'
import { useModalContentTransition } from 'src/common/functions'
import { postMessageFromWebviewToApp } from 'src/common/webview'

const props = defineProps({
  modelValue: Boolean,
  hasClose: {
    type: Boolean,
    default: false,
  },
  modalId: {
    type: String,
    default: '',
  },
  title: {
    type: String,
    default: '',
  },
  isBigSize: {
    type: Boolean,
    default: false,
  },
  body: {
    type: String,
    default: null,
  },
  bodyHtml: {
    type: String,
    default: null,
  },
  footer: {
    type: Boolean,
    default: true,
  },
  customBody: {
    type: Boolean,
    default: false,
  },
  actions: {
    type: Array,
    default: null,
  },
  component: {
    type: [Function, Object],
    default: null,
  },
  props: {
    type: Object,
    default: () => ({}),
  },
  contentClass: {
    type: [String, Array],
    default: '',
  },
  contentTransition: {
    type: [String, Object],
    default: '',
  },
  overlayClass: {
    type: [Object, Array, String],
    default: '',
  },
  overlayStyle: {
    type: [String, Object, Array],
    default: null,
  },
  hideOverlay: {
    type: Boolean,
    default: false,
  },
  persistent: {
    type: Boolean,
    default: false,
  },
  resolve: {
    type: Function,
    default: null,
  },
  reject: {
    type: Function,
    default: null,
  },
  horizontalAlign: {
    type: String,
    default: 'center',
  },
})

const emit = defineEmits(['update:model-value'])
const mediaQueries = inject('mediaQueries')
const slots = useSlots()
const $helpers = useHelpers()
const route = useRoute()
const router = useRouter()

const loadingObject = ref({})

const isTab = computed(() => mediaQueries.mobile)
const resolvedContentTransition = useModalContentTransition(
  computed(() => props.contentTransition),
  mediaQueries,
)

const modalWidth = computed(() => {
  if (isTab.value) {
    return '100%'
  }
  return props.isBigSize ? '934px' : '450px'
})

const modalTemplate = computed(() =>
  props.component
    ? markRaw(props.component)
    : markRaw(defineAsyncComponent(() => import('../customModal/ModalTemplate.vue'))),
)

const hasFooter = computed(() => {
  return (props.actions && props.actions.length) || (slots.footer && props.footer) || props.hasClose
})

const isTouch = computed(() => $helpers.platform.has.touch)

watch(
  () => props.actions,
  (data) => {
    if (Array.isArray(data)) {
      data.forEach((action, id) => {
        loadingObject.value[id] = false
      })
    }
  },
  { immediate: true },
)

function onOk(event) {
  props.resolve(event)
  closeModal()
}

async function modalAction(index, data) {
  if (!data.action) {
    throw new Error('Action is not exist')
  }
  if (data.loading) {
    loadingObject.value[index] = true
    try {
      await data.action()
    } catch {
      throw new Error('You must use Promise action with loading props === true')
    } finally {
      closeModal()
    }
  } else {
    if (data.action !== 'close') {
      data.action()
    }
    closeModal()
  }
}

function onCancel(value) {
  if (props.reject) {
    props.reject(value)
  }
}

function closeModal(value) {
  document.documentElement.style.scrollBehavior = 'auto'
  setTimeout(() => {
    document.documentElement.style.scrollBehavior = null
  }, 100)

  if (useSiteInfoStore().isWebView) {
    postMessageFromWebviewToApp({ type: 'modal', value: 'closed' })
  }
  loadingObject.value = {}
  onCancel(value)
  emit('update:model-value')
  if (route.query.modal) {
    router.replace({ 'query.modal': null })
  }
}
</script>

<style lang="scss">
.vfm {
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1052 !important;

  &[horizontal-align='right'] {
    justify-content: flex-end;

    .vfm__content {
      justify-content: flex-end;
    }
  }

  &[horizontal-align='left'] {
    justify-content: flex-start;

    .vfm__content {
      justify-content: flex-start;
    }
  }

  &__overlay {
    background-color: rgba(#121212, 0.6);
  }

  &__content {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (min-width: 768px) {
      max-height: min-content;
      max-width: min-content;
    }
  }
}

.touch-device {
  .custom-modal {
    pointer-events: all !important;
  }
}

.custom-modal {
  width: 100%;
  background-color: var(--cas-main-deep-2);
  color: var(--platform-text-2);
  border-radius: var(--radius-3);
  overflow: visible !important;
  display: flex;
  flex-direction: column;
  position: relative;
  max-height: 100% !important;
  max-width: none !important;

  @media (max-width: 768px) {
    height: 100dvh;
    border-radius: 0;
  }

  @media (min-width: 769px) {
    min-width: 450px;
    max-width: 80vw !important;
  }

  &__header-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: 64px;
    color: var(--color-text-primary);
    border-radius: var(--radius-3) var(--radius-3) 0 0;
    flex: none;

    @media (max-width: 768px) {
      border-radius: 0;
    }

    &:not(:empty) {
      background-color: var(--cas-main-deep-3);
      height: auto;
      padding: 16px 56px 16px 16px;

      @media (min-width: 768px) {
        padding: 24px 56px 24px 24px;
      }

      @media (min-width: 1024px) {
        padding-right: 24px;
      }
    }
  }

  &__header {
    display: -webkit-box;
    line-clamp: 2;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1 1;
  }

  &__back-btn {
    display: flex;
    align-items: center;
    column-gap: 4px;
    width: 75px;
    margin: 20px 0 -17px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 16px;
    font-weight: 700;
    line-height: 20px;
    cursor: pointer;

    @media (max-width: 767px) {
      margin: 20px 0 -12px;
    }
  }

  &__close-btn {
    position: absolute;
    top: 20px;
    right: 16px;
    z-index: 1;
    width: 24px;
    height: 24px;
    margin: auto;
    flex-shrink: 0;
    font-size: 24px;
    display: flex;
    align-items: center;
    cursor: pointer;
    color: var(--platform-text-10);
    transition: 0.3s ease;

    i {
      height: 24px;
    }

    &:active {
      color: var(--platform-text-8);
    }

    @media (min-width: 600px) {
      &:hover {
        color: var(--platform-text-8);
      }
    }

    @media (min-width: 768px) {
      top: 22px;
      right: 20px;
    }
  }

  &__body {
    font-size: 16px;
    padding: 16px;
    overflow: auto;
    flex: 1 1;

    p {
      margin-bottom: 1em;
    }

    @media (min-width: 768px) {
      padding: 24px;
    }

    &--ios {
      padding-bottom: 56px;
    }
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;

    .custom-modal--big-size & {
      @media (min-width: 768px) {
        flex-direction: row;
        justify-content: flex-end;

        & > button {
          flex: 1 1;
        }
      }
    }
  }

  &__footer {
    flex: none;
    padding: 16px 16px 24px 16px;
    border-top: 1px solid var(--cas-light-200);
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media (min-width: 600px) {
      flex-direction: row-reverse;
      flex-wrap: wrap;
      justify-content: flex-start;
    }

    @media (min-width: 768px) {
      padding: 24px;
    }
  }
}

.vfm-slide-fade-right-enter-active,
.vfm-slide-fade-right-leave-active {
  transition: all 0.3s ease-in-out;
}

.vfm-slide-fade-right-enter-from,
.vfm-slide-fade-right-leave-to {
  transform: translateX(360px);
  opacity: 0;
}
</style>
